import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './scheduled-maintenance.scss';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable, Icon, useModal, Button, ButtonColor, ButtonVariant, ButtonType, Form } from '@ampol/design-system';
import {
  getScheduledMaintenanceList,
  getScheduledMaintenance,
  deleteScheduledMaintenance,
} from 'actions/maintenanceActions';
import { yupDateTransformer } from 'utilities/dayjs-utilities';

import * as yup from 'yup';
import PageHeader from 'components/layout/page-header/page-header';
import ContentControls from 'components/content-controls/content-controls';
import ScheduledMaintenanceForm from 'features/maintenance/components/scheduled-maintenance-form/scheduled-maintenance-form';
import dayjs from 'dayjs';
import DeprecatedIcon from '../../../../assets/svg-icons';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';
import { trackCustomEvent } from 'utilities/app-insights';
import FilterControl from '../../../../components/filter-control/filter-control';
import { selectScheduledMaintenancePageContext } from '../../selectors/scheduledMaintenanceSelectors';
import { useScheduledMaintenanceModals } from './hooks/useScheduledMaintenanceModals';

/**
 * Scheduled Maintenance Page
 */
const ScheduledMaintenance = () => {
  const dispatch = useDispatch();

  const {
    scheduledMaintenance,
    scheduledMaintenanceLoading,
    scheduledMaintenanceError,
    scheduledMaintenanceList,
    scheduledMaintenanceListSize,
    scheduledMaintenanceListLoading,
    scheduledMaintenanceListError,
    createScheduledMaintenanceLoading,
    createScheduledMaintenanceLoaded,
    updateScheduledMaintenanceLoading,
    updateScheduledMaintenanceLoaded,
    deleteScheduledMaintenanceLoading,
    deleteScheduledMaintenanceLoaded,
    loggedInUser,
    assignedAccountNumber,
  } = useSelector(selectScheduledMaintenancePageContext);

  const handleDeleteSchedule = useCallback(() => {
    dispatch(deleteScheduledMaintenance(scheduledMaintenance?.id));

    trackCustomEvent(
      'Submit Delete Scheduled Maintenance',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Delete to Delete Scheduled Maintenance.',
      { selectedScheduledMaintenance: scheduledMaintenance?.id }
    );
  }, [dispatch, scheduledMaintenance?.id, loggedInUser, assignedAccountNumber]);

  // Modal management hook
  const {
    showModal,
    setShowModal,
    isEditSchedule,
    isSubmitValid,
    handleOpenAddModal,
    handleOpenEditModal,
    handleAddEditScheduleStateChange,
    getModalConfig,
  } = useScheduledMaintenanceModals({
    scheduledMaintenance,
    scheduledMaintenanceLoading,
    scheduledMaintenanceError,
    createScheduledMaintenanceLoading,
    createScheduledMaintenanceLoaded,
    updateScheduledMaintenanceLoading,
    deleteScheduledMaintenanceLoading,
    loggedInUser,
    assignedAccountNumber,
    onDelete: handleDeleteSchedule,
  });

  // Memoize modal config so useModal receives a stable object reference across renders.
  // getModalConfig() returns a new plain object on each call; wrapping it in useMemo
  // prevents scheduledMaintenanceModal from changing identity on every render, which
  // would otherwise cause the sync effect below to fire on every re-render.
  const modalConfig = useMemo(
    () => getModalConfig(),
    // getModalConfig is a useCallback that already tracks all its deps (loading flags,
    // error state, isEditSchedule, isSubmitValid, etc.). Using it as the sole dep here
    // ensures modalConfig recomputes exactly when any config-relevant value changes.
    [getModalConfig]
  );
  const scheduledMaintenanceModal = useModal({
    ...modalConfig,
    content: (
      <ScheduledMaintenanceForm
        formId={'scheduled-maintenance-form'}
        isEdit={isEditSchedule}
        onStateChange={handleAddEditScheduleStateChange}
      />
    ),
    color: ButtonColor.Blue,
    variant: 'double',
  });

  // Store modal reference so the effect below never runs with a stale modal instance
  const scheduledMaintenanceModalRef = useRef(scheduledMaintenanceModal);
  useEffect(() => {
    scheduledMaintenanceModalRef.current = scheduledMaintenanceModal;
  });

  // Sync modal visibility with state.
  // Uses ref to avoid depending on scheduledMaintenanceModal's identity directly,
  // which could change if useModal returns a new object on each render.
  useEffect(() => {
    if (showModal) {
      scheduledMaintenanceModalRef.current.open();
    } else {
      scheduledMaintenanceModalRef.current.close();
    }
  }, [showModal]);

  const defaultFilterValues = {
    startDate: null,
    endDate: null,
    searchTerm: '',
  };

  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const [sortColumn, setSortColumn] = useState('startDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterValues, setFilterValues] = useState({ ...defaultFilterValues });
  const [searchValues, setSearchValues] = useState({
    ...defaultFilterValues,
    startDate: '',
    endDate: '',
    pageSize: 10,
    pageNumber: 1,
  });

  const [filterBarEnabled, setFilterBarEnabled] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);

  const filterFormSchema = yup.object().shape({
    searchTerm: yup
      .string()
      .matches(
        Regex.SCHEDULED_MAINTENANCE_ALPHA_NUM_SPACE_SPECIAL,
        Validation.SCHEDULED_MAINTENANCE_ALPHA_NUM_SPACE_SPECIAL
      )
      .max(100, Validation.MAX_CHAR_ONE_HUNDRED),
    startDate: yup.date().nullable().transform(yupDateTransformer),
    endDate: yup
      .date()
      .nullable()
      .transform(yupDateTransformer)
      .min(yup.ref('startDate'), Validation.END_DATE_AFTER_START),
  });

  const getList = useCallback(() => {
    dispatch(
      getScheduledMaintenanceList(
        searchValues.startDate,
        searchValues.endDate,
        pageNumber,
        pageSize,
        sortColumn,
        sortDirection,
        searchValues.searchTerm
      )
    );
  }, [
    dispatch,
    searchValues.startDate,
    searchValues.endDate,
    pageNumber,
    sortColumn,
    sortDirection,
    searchValues.searchTerm,
  ]);

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (createScheduledMaintenanceLoaded || updateScheduledMaintenanceLoaded || deleteScheduledMaintenanceLoaded) {
      setShowModal(!(deleteScheduledMaintenanceLoaded || createScheduledMaintenanceLoaded));
      getList();
    }
  }, [
    createScheduledMaintenanceLoaded,
    updateScheduledMaintenanceLoaded,
    deleteScheduledMaintenanceLoaded,
    getList,
    setShowModal,
  ]);

  const handleEditScheduleRow = useCallback(
    (id) => {
      dispatch(getScheduledMaintenance(id));

      trackCustomEvent(
        'Open Edit Scheduled Maintenace Modal',
        loggedInUser,
        assignedAccountNumber,
        'Clicked a scheduled maintenance row to open its modal',
        { selectedScheduledMaintenance: id }
      );

      handleOpenEditModal();
    },
    [dispatch, loggedInUser, assignedAccountNumber, handleOpenEditModal]
  );

  const onTableColumnSorted = (column, isAscending) => {
    setSortColumn(column);
    setSortDirection(isAscending ? 'asc' : 'desc');
  };

  const onPageNumberChange = (value) => {
    setPageNumber(value);
    trackCustomEvent(
      'Change Page Number',
      loggedInUser,
      assignedAccountNumber,
      'Clicked to change page of scheduled maintenance data.'
    );
  };

  const handleToggleFilter = () => {
    setFilterBarEnabled(!filterBarEnabled);
  };

  const handleChangeFilter = (values) => {
    setFilterValues({
      ...values,
    });
  };

  const handleClearFilter = () => {
    window.dataLayer.push({
      event: 'Card_Portal_All_Clear_All',
      button_click: true,
    });
    setFilterValues({
      ...defaultFilterValues,
    });
  };

  const handleSubmitFilter = (values) => {
    const submittedValues = {
      ...values,
      startDate: values.startDate ? dayjs(values.startDate) : '',
      endDate: values.endDate ? dayjs(values.endDate) : '',
      searchTerm: values.searchTerm ? values.searchTerm : '',
    };

    handleToggleFilter();

    if (JSON.stringify(submittedValues) === JSON.stringify(searchValues)) {
      return;
    }

    setSearchValues(submittedValues);
    setFilterApplied(
      JSON.stringify({ ...defaultFilterValues, startDate: '', endDate: '' }) !== JSON.stringify(submittedValues)
    );

    trackCustomEvent(
      'Click Filter Scheduled Maintenance',
      loggedInUser,
      assignedAccountNumber,
      'Filter Scheduled Maintenance by Title or Date Range.'
    );
  };

  return (
    <div data-testid='scheduled-maintenance-root'>
      <PageHeader showButton={true} buttonLabel='Add schedule' onButtonClick={handleOpenAddModal} />
      <FilterControl
        showFilterControl={true}
        filterToggle={handleToggleFilter}
        filterDisplay={filterBarEnabled}
        filterApplied={filterApplied}
      >
        <div className='scheduled-maintenance__filter-zone'>
          <Form
            id={'scheduled-maintenance-filter-form'}
            schema={filterFormSchema}
            onSubmit={handleSubmitFilter}
            defaultValues={{ ...filterValues }}
            onChange={handleChangeFilter}
          >
            <div className='scheduled-maintenance__filter-content'>
              <div className='scheduled-maintenance__filter-content__subtitle0'></div>
              <Form.Input label={'Title'} name={'searchTerm'} />

              <div className='scheduled-maintenance__filter-content__subtitle1'>Maintenance date (range)</div>
              <div className='scheduled-maintenance__filter-dates'>
                <div className='scheduled-maintenance__filter-date'>
                  <Form.DatePicker
                    name='startDate'
                    label='Start Date'
                    placeholder={'DD/MM/YYYY'}
                    format={'DD/MM/YYYY'}
                    inputSuffix={
                      <SuffixWrapper>
                        <DeprecatedIcon iconName='calender' />
                      </SuffixWrapper>
                    }
                  />
                </div>
                <span className={'scheduled-maintenance__range-splitter'} />
                <div className='scheduled-maintenance__filter-date'>
                  <Form.DatePicker
                    name='endDate'
                    label='End Date'
                    placeholder={'DD/MM/YYYY'}
                    format={'DD/MM/YYYY'}
                    inputSuffix={
                      <SuffixWrapper>
                        <DeprecatedIcon iconName='calender' />
                      </SuffixWrapper>
                    }
                  />
                </div>
              </div>
              <div className='scheduled-maintenance__filter-buttons'>
                <Button
                  variant={ButtonVariant.Secondary}
                  color={ButtonColor.Blue}
                  label='Clear All'
                  type={ButtonType.Button}
                  onClick={handleClearFilter}
                />
                <Button
                  variant={ButtonVariant.Primary}
                  color={ButtonColor.Blue}
                  label='Filter'
                  type={ButtonType.Submit}
                />
              </div>
            </div>
          </Form>
        </div>
      </FilterControl>
      <ContentControls
        showPageControls={true}
        pageNumber={searchValues.pageNumber}
        itemCount={scheduledMaintenanceListSize}
        onPageChange={onPageNumberChange}
        showFilterControl={false}
        filterToggle={handleToggleFilter}
        filterDisplay={filterBarEnabled}
        filterApplied={filterApplied}
      />
      <div className={'card-maintenance__table-container'}>
        <DataTable
          caption={'Maintenance Schedules'}
          columns={[
            {
              header: 'Title',
              accessorKey: 'title',
            },
            {
              header: 'Start Date',
              accessorFn: (x) => dayjs(x.startDate).format('DD/MM/YYYY HH:mm'),
              defaultSorted: 'desc',
              accessorKey: 'startDate',
            },
            {
              header: 'End Date',
              accessorFn: (x) => dayjs(x.endDate).format('DD/MM/YYYY HH:mm'),
              accessorKey: 'endDate',
            },
            {
              id: 'action',
              accessorFn: (x) => (
                <>
                  <button className={'card-maintenance__edit'} onClick={() => handleEditScheduleRow(x.id)}>
                    <Icon url={'/icons/edit.svg'} />
                  </button>
                </>
              ),
              rtl: true,
            },
          ]}
          onColumnSorted={onTableColumnSorted}
          isLoading={scheduledMaintenanceListLoading}
          columnTemplate={'1fr 1fr 1fr 70px'}
          data={scheduledMaintenanceList}
          tableVariant='list'
          onRowClick={(row) => handleEditScheduleRow(row.id)}
          noDataMessage={
            scheduledMaintenanceListError
              ? 'Oops! There was an error. Please try again later or report this issue.'
              : 'No scheduled maintenances available'
          }
        />
      </div>
    </div>
  );
};

export default ScheduledMaintenance;
